import React, {useEffect, useRef, useState} from "react";
import {Box, Button, FormHelperText} from "@mui/material";
import styled from "@emotion/styled";
import {useNavigate, useParams} from "react-router-dom";
import {AuthAlert, AuthDividerLink, AuthHeader, AuthPageLayout} from "../../components/AuthShell";
import AccountRecoveryLinks from "../../components/AuthShell/AccountRecoveryLinks";
import {validateCompany, validateConfirmPassword, validateEmail, validateFirst, validateLast, validatePassword}
  from "../../helper/validation";
import TextFieldBox from "../../components/Common/TextFieldBox";
import {userSignup, validateInviteCode} from "../../api/adminApi";
import {useRuntimeConfig} from "../../config/RuntimeConfigProvider";
import {errorDetail} from "../../helper/errorDetail";
import {FieldLabel, primaryButtonSx} from "../Onboarding/registrationShared";


const FIELD_SX = {mb: 2.25};

// TODO: Delete this once no longer referenced
export const ErrorMessage = styled("div")(() => ({
  marginTop: "5px",
  marginBottom: "8px",
  color: "red",
  fontSize: "12px",
}));

const Signup = () => {
  const navigate = useNavigate();
  const {PUBLIC_SIGNUP_ENABLED} = useRuntimeConfig();
  const publicSignupEnabled = PUBLIC_SIGNUP_ENABLED === "true";

  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [confirmPassword, setConfirmPassword] = useState();
  const [companyName, setCompanyName] = useState();
  const [phoneCountry, setPhoneCountry] = useState("US");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneCountries, setPhoneCountries] = useState([{country: "US", name: "United States", callingCode: "1"}]);
  const [phoneUnavailable, setPhoneUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    import("../../helper/signupPhone").then((module) => {
      if (active) setPhoneCountries(module.phoneCountries);
    }).catch(() => { if (active) setPhoneUnavailable(true); });
    return () => { active = false; };
  }, []);

  const [validationError, setValidationError] = useState();
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { inviteCode } = useParams();
  const [isInviteSignup, setIsInviteSignup] = useState(Boolean(inviteCode));
  const [inviteValidationStatus, setInviteValidationStatus] = useState(
    inviteCode ? "validating" : "not-required"
  );
  const requestInFlight = useRef(false);


  useEffect( () => {
    let isCurrent = true;

    if (!inviteCode) {
      setIsInviteSignup(false);
      setInviteValidationStatus("not-required");
      return () => {
        isCurrent = false;
      };
    }

    setIsInviteSignup(true);
    setInviteValidationStatus("validating");
    validateInviteCode(inviteCode)
      .then((res) => {
          if (!isCurrent) return;

          if (res?.status === 200) {
            setEmail(res?.data?.email);
            setInviteValidationStatus("valid");
          } else if ([400, 404, 410, 422].includes(res?.status)) {
            setIsInviteSignup(!publicSignupEnabled);
            setInviteValidationStatus("invalid");
            setEmail("");
          } else {
            setInviteValidationStatus("error");
            setValidationError(
              "We could not validate this invitation. Please refresh the page and try again."
            );
          }
        }
      );

    return () => {
      isCurrent = false;
    };
  }, [inviteCode, publicSignupEnabled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestInFlight.current) return;
    if (isInviteSignup && inviteValidationStatus !== "valid") {
      setValidationError(
        "We could not validate this invitation. Please refresh the page and try again."
      );
      return;
    }

    requestInFlight.current = true;
    setEmailAlreadyRegistered(false);
    setSubmitting(true);
    const normalizedEmail = isInviteSignup ? email : email?.trim();
    const normalizedPhone = phoneNumber.trim()
      ? await import("../../helper/signupPhone")
        .then(({normalizeSignupPhone}) => normalizeSignupPhone(phoneNumber, phoneCountry))
        .catch(() => ({phone: undefined, error: "We could not check the phone number. Please try again."}))
      : {phone: undefined, error: ""};
    setPhoneError(normalizedPhone.error);

    const errors = [
      validateFirst(firstName),
      validateLast(lastName),
      ...(isInviteSignup ? [
        validatePassword(password),
        validateConfirmPassword(password, confirmPassword),
      ] : [validateEmail(normalizedEmail)]),
      validateCompany(companyName),
      normalizedPhone.error,
    ].filter((e) => e !== "");

    if (errors.length > 0) {
      requestInFlight.current = false;
      setSubmitting(false);
      setValidationError(errors[0] === normalizedPhone.error ? "" : errors[0]);
    } else {
      setValidationError("");

      const userdata = {
        first_name: firstName,
        last_name: lastName,
        email: normalizedEmail,
        company: companyName,
      };
      if (normalizedPhone.phone) userdata.phone = normalizedPhone.phone;
      if (isInviteSignup) {
        userdata.password = password;
        userdata.invite_code = inviteCode;
      }

      const res = await userSignup(userdata);
      requestInFlight.current = false;
      setSubmitting(false);

      const {detail} = res?.data || {};
      const expectedStatus = isInviteSignup ? 200 : 202;
      if (res?.status === expectedStatus) {
        navigate("/verify-email/check", {state: {email: normalizedEmail}});
      } else if (res?.status === 422 && Array.isArray(detail) &&
        detail.some(({loc}) => loc?.includes("phone"))) {
        setPhoneError("Check the phone number and country code, then try again.");
      } else if (!isInviteSignup && res?.status === 409 &&
        detail?.error_code === "EMAIL_ALREADY_REGISTERED") {
        setEmailAlreadyRegistered(true);
        setValidationError(errorDetail(detail, "An account with this email already exists."));
      } else if (!isInviteSignup) {
        setValidationError(
          res?.status === 429
            ? "Too many signup attempts. Please try again later."
            : "We could not complete your signup. Check your connection and try again."
        );
      } else {
        setValidationError(
          errorDetail(detail,
            "We could not complete your signup. Check your connection and try again."
          )
        );
      }
    }
  };


  const handlePasswordInputChange = (e) => {
    setPassword(e.target.value);
    setValidationError(validatePassword(e.target.value));
  }

  const handleConfirmPasswordInputChange = (e) => {
    setConfirmPassword(e.target.value);
    setValidationError(validateConfirmPassword(password, e.target.value))
  }

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value)
    setValidationError(validateFirst(e.target.value))
  }

  const handleLastNameChange = (e) => {
    setLastName(e.target.value)
    setValidationError(validateLast(e.target.value))
  }

  const handleCompanyNameChange = (e) => {
    setCompanyName(e.target.value)
    setValidationError(validateCompany(e.target.value))
  }

  const handleEmailChange = (e) => {
    setEmailAlreadyRegistered(false);
    setEmail(e.target.value);
    setValidationError(validateEmail(e.target.value.trim()));
  }

  if (inviteValidationStatus === "invalid" && !publicSignupEnabled) {
    return (
      <AuthPageLayout testId="signup-page">
        <AuthHeader title="This invitation is not valid" />
        <AuthAlert>Ask the person who invited you for a new invitation link.</AuthAlert>
      </AuthPageLayout>
    );
  }

  const field = ({id, label, required = true, ...props}) => (
    <Box sx={FIELD_SX}>
      <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>
      <TextFieldBox fullWidth size="small" name={id} inputProps={{id}} {...props} />
    </Box>
  );

  return (
    <AuthPageLayout testId="signup-page">
      <AuthHeader title="Create your account" subtitle="Tell us a bit about yourself to get started." />
      <AuthAlert>{validationError}</AuthAlert>
      {emailAlreadyRegistered && <AccountRecoveryLinks />}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, columnGap: 2}}>
          {field({id: "first_name", label: "First Name", placeholder: "First name", handleChange: handleFirstNameChange})}
          {field({id: "last_name", label: "Last Name", placeholder: "Last name", handleChange: handleLastNameChange})}
        </Box>
        {field({id: "email", label: "Email", placeholder: "Email", required: !isInviteSignup, disabled: isInviteSignup || submitting,
          value: email || "", handleChange: isInviteSignup ? undefined : handleEmailChange})}
        {isInviteSignup && (
          <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, columnGap: 2}}>
            {field({id: "new_password", label: "New Password", type: "password", placeholder: "New Password*",
              handleChange: handlePasswordInputChange})}
            {field({id: "confirm_password", label: "Confirm Password", type: "password", placeholder: "Confirm Password*",
              handleChange: handleConfirmPasswordInputChange})}
          </Box>
        )}
        {field({id: "company_name", label: "Company", placeholder: "Company name", handleChange: handleCompanyNameChange})}
        <Box sx={FIELD_SX}>
          <FieldLabel htmlFor="phone" required={false}>Phone (optional)</FieldLabel>
          <Box sx={{display: "flex", gap: 1}}>
            <Box
              component="select"
              id="phone-country"
              aria-label="Country code"
              title={phoneCountries.find(({country}) => country === phoneCountry)?.name}
              disabled={phoneUnavailable}
              value={phoneCountry}
              onChange={(event) => {
                setPhoneCountry(event.target.value);
                setPhoneError("");
              }}
              sx={{
                boxSizing: "border-box", width: 125, flexShrink: 0, minWidth: 0,
                border: "1px solid #C7D0D9", borderRadius: 1, bgcolor: "#FFFFFF",
                px: 1, fontSize: 14, color: "#1A202C",
                "&:focus-visible": {outline: "2px solid #1976D2", outlineOffset: 2},
              }}
            >
              {phoneCountries.map(({country, name, callingCode}) => (
                <option key={country} value={country}>{country} +{callingCode} — {name}</option>
              ))}
            </Box>
            <TextFieldBox
              fullWidth size="small" name="phone" type="tel" placeholder="Phone number"
              disabled={phoneUnavailable}
              value={phoneNumber}
              handleChange={(event) => {
                setPhoneNumber(event.target.value);
                setPhoneError("");
              }}
              inputProps={{id: "phone", inputMode: "tel", autoComplete: "tel-national",
                "aria-invalid": Boolean(phoneError), "aria-describedby": phoneError ? "phone-error" : undefined}}
              sx={{minWidth: 0}}
            />
          </Box>
          {phoneUnavailable && <FormHelperText>Phone entry is temporarily unavailable. You can continue without a number.</FormHelperText>}
          {phoneError && <FormHelperText id="phone-error" error>{phoneError}</FormHelperText>}
        </Box>

        <Button
          variant="contained"
          type="submit"
          fullWidth
          disableElevation
          disabled={submitting || (isInviteSignup && inviteValidationStatus !== "valid")}
          sx={{...primaryButtonSx, mt: 0.5}}
        >
          {submitting ? "Sending…" : isInviteSignup ? "Create account" : "Send registration link"}
        </Button>
      </Box>

      <AuthDividerLink prompt="Already have an account?" to="/">Log in</AuthDividerLink>
    </AuthPageLayout>
  );
}

export default Signup;
